import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Numeral from 'numeral';
import { Button, Copy, Link, Tabs, Tag } from '@hexure/ui';
import { Icon } from '@mdi/react';
import {
  mdiCheckboxBlankCircleOutline,
  mdiCheckboxOutline,
  mdiCheckboxBlankOutline,
  mdiCheckboxMarkedCircle,
  mdiChevronRight,
  mdiChevronDown,
  mdiChevronUp,
  mdiCheckboxMarkedCircleOutline
} from '@mdi/js';

import Loader from 'shared/Loader';
import Tooltip from 'shared/Tooltip';
import AmbestInfo from 'shared/AmbestInfo';

import { ThemeContext } from 'shared/ThemeContext';

import { Colors } from 'constants/Clementine';

import ApiUtils from 'utils/AnnuitiesApi';
import ProductDetail from './ProductDetail';
import RiderInfo from './RiderInfo';
import RatesInfo from './RatesInfo';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
`;

const Checkbox = styled.div`
  display: flex;
  padding: 16px 20px 0px 0px;
  align-items: flex-start;
  align-self: stretch;
  cursor: pointer;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  border-radius: 8px;
  border-width: ${props => (props.$isSelected ? '2px' : '1px')};
  border-style: solid;
  border-color: ${props => (props.$isSelected ? props.$color : '#E5E5E5')};
  background: #fff;
`;

const Top = styled.div`
  display: flex;
  flex: 1;
  padding: 16px;
  align-items: center;
  gap: 16px;
  justify-content: space-between;
  background: ${props => `${props.$color}0d`};
`;

const TopLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Logo = styled.img`
  height: 30px;
  width: auto;
`;

const ProductName = styled.h5`
  color: #000;
  font-family: Roboto;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  margin: 0;
  padding: 0;
`;

const Bottom = styled.div`
  display: flex;
  padding: 16px;
  justify-content: space-between;
`;

const BottomLeft = styled.div`
  align-self: stretch;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: space-between;
`;

const Meta = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  flex-wrap: wrap;
  flex: 1;
`;

const TagLabel = styled.span`
  font-style: italic;
  margin-right: 4px;
`;

const TagValue = styled.span`
  font-weight: 600;
`;

const BottomRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const CalculatedValue = styled.div`
  color: #000;
  text-align: right;
  font-family: Roboto;
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  align-self: stretch;
  border-bottom: 1px solid #e5e5e5;
  padding: 0px 12px 12px 0px;
`;

const Rates = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const Rate = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 12px 0px 12px;
  align-items: flex-end;
  gap: 4px;
`;

const RateDivider = styled.div`
  width: 1px;
  background: #e5e5e5;
  align-self: stretch;
`;

const RateValue = styled.span`
  color: #000;
  font-family: Roboto;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
`;

const RateLabel = styled.span`
  color: #999;
  font-family: Roboto;
  font-size: 13px;
  font-style: italic;
  font-weight: 400;
  line-height: 14px;
`;

const Details = styled.div`
  background: #fff;
  border-radius: 8px;
`;

const TabContent = styled.div`
  padding: 30px;
`;

const Features = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Feature = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const ApproximateIncome = styled.span`
  color: #999;
  font-family: Roboto;
  font-size: 13px;
  font-style: italic;
  font-weight: 400;
  line-height: 14px;
`;

const ValueWithTooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: help;
  user-select: text;
`;

const TableHeader = styled.table`
  width: 100%; 
  border-collapse: collapse; 
  border: 1px solid #e5e5e5;
`;

const TableTitle = styled.td`
  padding: 16px;
  border: 1px solid #ddd;
  font-weight: 500;
`;

const TableContent = styled.td`
  padding: 16px;
  border: 1px solid #ddd;
`;

const QuoteCard = ({
  hasActions,
  index,
  isSelected,
  onSelect,
  selectedQuotes,
  showActionsBtnText = 'Get Started',
  onShowActions,
  onStartEApp,
  quote,
  carrier,
  product,
  solveFor,
  productType,
  isUniversalQuoter = false,
  showDetails = false,
  isStartEAppBtnEnabled = true
}) => {
  const { color } = useContext(ThemeContext);
  const [show_details, setShowDetails] = useState(showDetails);
  const [active_tab, setActiveTab] = useState('features');
  const [loading, setLoading] = useState(false);
  const [ambest_record, setAmbestRecord] = useState(null);
  const [productDetail, setProductDetail] = useState(null);
  const [riderInfo, setRiderInfo] = useState(null);
  const [ratesInfo, setRatesInfo] = useState(null);

  //useEffect for props change
  useEffect(() => {
    setShowDetails(showDetails);
  }, [showDetails]);

  // Helper function to round rates to nearest 0.05%
  const roundToNearestFiveHundredths = rate => {
    if (!rate && rate !== 0) return 0;
    // Convert to percentage if needed
    const percentageRate = rate / 100;
    // Round to nearest 0.0005 (which is 0.05%)
    return Math.round(percentageRate / 0.0005) * 0.0005;
  };

  // Helper function to get tooltip message for accumulation calculation
  const getAccumulationTooltip = () => {
    if (solveFor !== 'Accumulation') return null;

    if (productType === 'Fixed Indexed Annuity') {
      return (
        <div>
          <p style={{ margin: '0 0 10px 0' }}>
            The accumulation value is calculated using the 1 Year Point to Point Account Connected with the S&P 500 Index, and using returns from prior years of performance.
          </p>
          <p style={{ margin: 0 }}>
            A 5-year accumulation will be calculated based on the S&P 500 performance for each prior 12-month crediting period, so that calculation would match real performance over last 5 years.
          </p>
        </div>
      );
    }

    // Check if product has bonus (checking multiple possible bonus indicators)
    if (quote.bonus_rate) {
      return (
        <div>
          <p style={{ margin: '0 0 10px 0' }}>The accumulation value is calculated using the current interest rate, which is applied and compounded annually</p>
          <p style={{ margin: 0 }}>This product has a bonus rate that is increasing the initial interest rate</p>
        </div>
      );
    }

    // Default for Fixed/MYGA
    return 'The accumulation value is calculated using the current interest rate, which is applied and compounded annually';
  };

  const meta = [
    {
      label: 'Rating',
      raw_value: quote.am_best_rating === 'System.String[]' ? '' : quote.am_best_rating || 'A+',
      value: quote.am_best_rating === 'System.String[]' ? '' : quote.am_best_rating || 'A+'
    },
    {
      label: 'Rate GTD',
      raw_value: quote.gtd_rate_years,
      value: `${quote.gtd_rate_years} Years`
    },
    {
      label: 'Surrender Charge',
      raw_value: quote.surrender_charge,
      value: `${quote.surrender_charge} Years`
    },
    ...(quote?.index_name
      ? [
        {
          label: 'Index',
          raw_value: quote?.index_name,
          value: quote?.index_name
        }
      ]
      : [])
  ];

  const tabs = [
    { isActive: active_tab === 'features', label: 'Features', onClick: setActiveTab.bind(null, 'features') },
    { isActive: active_tab === 'overview', label: 'Carrier Overview', onClick: setActiveTab.bind(null, 'overview') },
    { isActive: active_tab === 'details', label: 'Details', onClick: setActiveTab.bind(null, 'details') }
  ];

  // Add rates tab based on context and product type
  if (isUniversalQuoter || productType === 'Fixed Indexed Annuity') {
    tabs.push({ isActive: active_tab === 'rates', label: 'Rates', onClick: setActiveTab.bind(null, 'rates') });
  }

  // Add rider info tab for FIA (both contexts)
  if (productType === 'Fixed Indexed Annuity') {
    tabs.push({ isActive: active_tab === 'rider_info', label: 'Rider Info', onClick: setActiveTab.bind(null, 'rider_info') });
  }

  if (product?.information) {
    tabs.push({ isActive: active_tab === 'information', label: 'Important Information', onClick: setActiveTab.bind(null, 'information') });
  }
  const calculated_value =
    solveFor === 'Accumulation'
      ? quote.accumulation
      : solveFor === 'Contribution'
        ? quote.premium
        : solveFor === 'Income'
          ? quote.monthly_income
          : solveFor === 'GMBIncome'
            ? quote.premium
            : quote.premium;

  useEffect(() => {
    if (active_tab === 'overview') {
      setLoading(true);
      ApiUtils.loadAmbestRecord(quote.carrier_id)
        .then(({ data }) => {
          setAmbestRecord(data[0]);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    } else if (active_tab === 'details' || (active_tab === 'rates' && productType !== 'Fixed Indexed Annuity')) {
      setLoading(true);
      ApiUtils.loadProductDetail(quote.id)
        .then(response => {
          setProductDetail(response.data);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    } else if (active_tab === 'rider_info') {
      setLoading(true);
      ApiUtils.loadRiderInfo(quote.id)
        .then(response => {
          setRiderInfo(Array.isArray(response.data) ? response.data : [response.data]);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    } else if (active_tab === 'rates' && productType === 'Fixed Indexed Annuity') {
      // For Universal Quoter FA/MYGA, we don't need to load ratesInfo API data
      // since we display quote data directly. Only load for FIA or regular annuity quoter.
      setLoading(true);
      ApiUtils.loadRatesInfo(quote.product_id)
        .then(response => {
          setRatesInfo(response.data);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, [active_tab]);
  const solveForMapping = {
    Accumulation: 'Approximated Accumulation',
    Contribution: 'Approximated Contribution',
    Income: 'Approximated Income',
    GMBIncome: 'Approximated Income'
  };
  const approximatedLabel = solveForMapping[solveFor] || 'Approximated Income';
  let riderInfoContent = null;
  if (active_tab === 'rider_info') {
    if (loading) {
      riderInfoContent = <Loader />;
    } else if (Array.isArray(riderInfo) && riderInfo.length > 0) {
      riderInfoContent = riderInfo.map((rider, idx) => <RiderInfo data={[rider]} key={idx} loading={false} />);
    } else {
      riderInfoContent = <Copy>No rider information available.</Copy>;
    }
  }
  return (
    <Wrapper>
      {/* Show checkbox only in individual quoter (not universal quoter) */}
      {!isUniversalQuoter && (
        <Checkbox id={`quote-checkbox-${index + 1 || ''}`} onClick={onSelect.bind(null, quote.id)}>
          <Icon color={selectedQuotes?.includes(quote.id) ? color : '#999'} path={selectedQuotes?.includes(quote.id) ? mdiCheckboxOutline : mdiCheckboxBlankOutline} size='28px' />
        </Checkbox>
      )}
      <Content $color={color} $isSelected={isSelected}>
        <Top $color={color}>
          <TopLeft>
            <Logo src={carrier?.logo?.url} />
            <ProductName>{product?.product_name || quote?.product_name}</ProductName>
          </TopLeft>
          {/*<Button*/}
          {/*  icon={mdiChevronRight}*/}
          {/*  onClick={modal.bind(null, 'visualize', product)}*/}
          {/*  small={true}*/}
          {/*>*/}
          {/*  Visualize*/}
          {/*</Button>*/}
          {hasActions ? (
            <Button icon={mdiChevronRight} onClick={onShowActions.bind(null, quote)} small={true}>
              {showActionsBtnText || 'Get Started'}
            </Button>
          ) : onStartEApp ? (
            <Button icon={mdiChevronRight} onClick={onStartEApp.bind(null, quote)} small={true} disabled={!isStartEAppBtnEnabled}>
              Start eApp
            </Button>
          ) : null}
        </Top>
        <Bottom>
          <BottomLeft>
            <Meta>
              {meta.map((m, i) => {
                if (m.raw_value) {
                  return (
                    <Tag color='SUBTLE_GRAY' key={i}>
                      <TagLabel>{m.label}</TagLabel>
                      <TagValue>{m.value}</TagValue>
                    </Tag>
                  );
                }

                return null;
              })}
            </Meta>
            <Link onClick={setShowDetails.bind(null, !show_details)} style={{ display: 'flex', alignItems: 'center' }}>
              {show_details ? (
                <>
                  Hide Details <Icon color={color} path={mdiChevronUp} size='18px' />
                </>
              ) : (
                <>
                  Show Details <Icon color={color} path={mdiChevronDown} size='18px' />
                </>
              )}
            </Link>
          </BottomLeft>
          <BottomRight>
            <CalculatedValue>
              {solveFor === 'Accumulation' && getAccumulationTooltip() ? (
                <ValueWithTooltip>
                  <span>{Numeral(calculated_value).format('$0,0.00')}</span>
                  <Tooltip message={getAccumulationTooltip()} width={300} position='left' />
                </ValueWithTooltip>
              ) : (
                <span>{Numeral(calculated_value).format('$0,0.00')}</span>
              )}
              <br />
              <ApproximateIncome>{approximatedLabel}</ApproximateIncome>
            </CalculatedValue>
            <Rates>
              <Rate>
                <RateValue>{Numeral(roundToNearestFiveHundredths(quote.base_rate)).format('0.00%')}</RateValue>
                <RateLabel>Guaranteed</RateLabel>
              </Rate>
              <RateDivider />
              <Rate>
                <RateValue>
                  {Numeral(roundToNearestFiveHundredths(productType === 'Fixed Indexed Annuity' ? 
                      (quote.cap_rate || quote.capRate) : quote.current_rate)).format('0.00%')
                  }
                </RateValue>
                <RateLabel>Current</RateLabel>
              </Rate>
            </Rates>
          </BottomRight>
        </Bottom>
        {show_details ? (
          <Details>
            <Tabs style={{ borderTop: 'none' }} tabs={tabs} />
            <TabContent>
              {active_tab === 'features' ? (
                <Features>
                  {product?.features.map((f, i) => (
                    <Feature key={i}>
                      <Icon color={Colors.GREEN.hex} path={mdiCheckboxMarkedCircleOutline} size='20px' />
                      <Copy>{f.name || 'N/A'}</Copy>
                    </Feature>
                  ))}
                </Features>
              ) : null}
              {active_tab === 'overview' ? <>{loading ? <Loader /> : <AmbestInfo details={ambest_record} id={`ambest-info-${quote.id}`} />}</> : null}
              {active_tab === 'details' ? <ProductDetail carrier={carrier} data={productDetail} productType={productType} isUniversalQuoter={isUniversalQuoter} /> : null}
              {active_tab === 'information' ? <Copy>{product.information}</Copy> : null}
              {active_tab === 'rates' ? loading ? <Loader /> :
                // Simplified rates view for Universal Quoter
                isUniversalQuoter ? productType === 'Fixed Indexed Annuity' ? (
                  // For FIA, show the existing RatesInfo component
                  <RatesInfo data={ratesInfo} loading={loading} />
                ) : (
                  <div style={{ padding: '20px' }}>
                    <TableHeader>
                      <tbody>
                        <tr style={{ backgroundColor: '#f2f2f2' }}>
                          <TableTitle>Current Fixed Rate</TableTitle>
                          <TableContent>{quote?.current_rate ? `${quote.current_rate.toFixed(2)}%` : 'N/A'}</TableContent>
                        </tr>
                        <tr>
                          <TableTitle>Guaranteed Period</TableTitle>
                          <TableContent>{quote?.gtd_period ? `${quote.gtd_period} Year${quote.gtd_period !== '1' ? 's' : ''}` : 'N/A'}</TableContent>
                        </tr>
                        <tr style={{ backgroundColor: '#f2f2f2' }}>
                          <TableTitle>GMIR (Guaranteed Minimum Interest Rate)</TableTitle>
                          <TableContent>{quote?.base_rate ? `${quote.base_rate.toFixed(2)}%` : 'N/A'}</TableContent>
                        </tr>
                        {productDetail?.bonus_break_point && productDetail?.bonus ? (
                          <>
                            <tr>
                              <TableTitle>Bonus Banding</TableTitle>
                              <TableContent>
                                {Array.isArray(productDetail.bonus_break_point) && productDetail.bonus_break_point.length > 0
                                  ? productDetail.bonus_break_point.map((bp, idx) => (
                                    <div key={idx}>
                                      ${Number(bp).toLocaleString()}+: {productDetail.bonus}
                                    </div>
                                  ))
                                  : 'N/A'}
                              </TableContent>
                            </tr>
                            <tr style={{ backgroundColor: '#f2f2f2' }}>
                              <TableTitle>Bonus Rate Type</TableTitle>
                              <TableContent>{productDetail?.bonus_type || 'N/A'}</TableContent>
                            </tr>
                          </>
                        ) : null}
                      </tbody>
                    </TableHeader>
                  </div>)
                  :
                  // Original RatesInfo for regular annuity quoter
                  (<RatesInfo data={ratesInfo} loading={loading} />)
                : null}
              {active_tab === 'rider_info' ? riderInfoContent : null}
            </TabContent>
          </Details>
        ) : null}
      </Content>
    </Wrapper>
  );
};

QuoteCard.propTypes = {
  carrier: PropTypes.object,
  hasActions: PropTypes.bool,
  index: PropTypes.number,
  isSelected: PropTypes.bool,
  modal: PropTypes.func,
  onSelect: PropTypes.func,
  showActionsBtnText: PropTypes.string,
  onShowActions: PropTypes.func,
  onStartEApp: PropTypes.func,
  product: PropTypes.object,
  quote: PropTypes.object,
  selectedQuotes: PropTypes.array,
  solveFor: PropTypes.string,
  productType: PropTypes.string,
  isUniversalQuoter: PropTypes.bool,
  showDetails: PropTypes.bool,
  isStartEAppBtnEnabled: PropTypes.bool
};

export default QuoteCard;
